import React, { useCallback, useEffect, useState } from 'react';
import {
  Button as MuiButton,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { FiCopy, FiPlus } from 'react-icons/fi';

import Header from '../../components/Header';
import StandardContainer from '../../components/StandardContainer';
import { useToast } from '../../hooks/toast';
import api from '../../services/api';
import CreateNotification, { NotificationForm } from './Create';
import { Content } from './styles';

const SAO_PAULO_TIMEZONE = 'America/Sao_Paulo';
const PAGE_SIZE = 10;

interface NotificationRow {
  id: number;
  title: string;
  notification_text: string;
  provider?: PushProvider;
  data?: string | Record<string, any>;
  send_date?: string;
  scheduled: boolean;
  status?: string;
  created_at?: string;
  image_url?: string | null;
  total_recipients?: number;
  total_opened?: number;
  open_rate?: number;
}

type PushProvider = 'expo' | 'firebase';

interface TokenSummary {
  provider: PushProvider;
  total: number;
}

interface PushTokenRow {
  id: number;
  token: string;
  platform?: string | null;
  last_seen_at?: string | null;
}

const parseData = (data?: string | Record<string, any>): Record<string, any> => {
  if (!data) return {};
  if (typeof data === 'object') return data;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: SAO_PAULO_TIMEZONE,
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const Notifications = () => {
  const { addToast } = useToast();
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [notificationProvider, setNotificationProvider] = useState<'all' | PushProvider>('all');
  const [providerTab, setProviderTab] = useState<PushProvider>('firebase');
  const [summary, setSummary] = useState<TokenSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [tokenRows, setTokenRows] = useState<PushTokenRow[]>([]);
  const [tokenRowsLoading, setTokenRowsLoading] = useState(false);
  const [tokenPage, setTokenPage] = useState(1);
  const [tokenTotal, setTokenTotal] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState<Partial<NotificationForm> | undefined>();

  const loadNotifications = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const response = await api.get('/notifications', {
        params: {
          search: search || undefined,
          provider: notificationProvider === 'all' ? undefined : notificationProvider,
          page,
          limit: PAGE_SIZE,
        },
      });
      const result = response.data;
      if (Array.isArray(result)) {
        setRows(result);
        setTotalRows(result.length);
      } else {
        setRows(result.data || []);
        setTotalRows(Number(result.total || 0));
      }
    } catch {
      if (showLoading) {
        addToast({
          type: 'error',
          title: 'Erro ao carregar notificações',
        });
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [addToast, notificationProvider, page, search]);

  useEffect(() => {
    if (activeTab === 0) {
      void loadNotifications();
    }
  }, [activeTab, loadNotifications]);

  useEffect(() => {
    if (activeTab !== 0) return;

    const refreshInterval = window.setInterval(() => {
      void loadNotifications(false);
    }, 15000);

    return () => window.clearInterval(refreshInterval);
  }, [activeTab, loadNotifications]);

  useEffect(() => {
    if (activeTab !== 1) return;

    setSummaryLoading(true);
    setTokenRowsLoading(true);
    Promise.all([
      api.get('/users/push-token/summary'),
      api.get('/users/push-token', {
        params: { provider: providerTab, page: tokenPage, limit: PAGE_SIZE },
      }),
    ])
      .then(([summaryResponse, tokenResponse]) => {
        setSummary(summaryResponse.data || []);
        setTokenRows(tokenResponse.data?.data || []);
        setTokenTotal(Number(tokenResponse.data?.total || 0));
      })
      .catch(() =>
        addToast({
          type: 'error',
          title: 'Erro ao carregar usuários',
        }),
      )
      .finally(() => {
        setSummaryLoading(false);
        setTokenRowsLoading(false);
      });
  }, [activeTab, addToast, providerTab, tokenPage]);

  const openCreateModal = (initialValues?: Partial<NotificationForm>) => {
    setFormInitialValues(initialValues);
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setFormInitialValues(undefined);
  };

  const handleNotificationCreated = useCallback(async () => {
    if (page !== 1) {
      setPage(1);
      return;
    }

    await loadNotifications();
  }, [loadNotifications, page]);

  const duplicateNotification = (row: NotificationRow) => {
    const data = parseData(row.data);
    openCreateModal({
      title: row.title,
      description: row.notification_text,
      notificationType: data.type === 'LINK' ? 'link' : 'default',
      url: data.url || '',
      tela: data.tela || data.screen || '',
      livro: data.livro || data.book || data.bookName || '',
      capitulo: data.capitulo || data.chapter || data.chapterNumber || '',
      versiculo: data.versiculo || data.verse || data.verseNumber || '',
      canal: data.canal || data.channel || '',
      camera: data.camera || data.screen_aparecida_ao_vivo || '',
      id: data.id || data.id_noticia || data.id_aparecida_play_video || '',
      image_url: row.image_url || '',
    });
  };

  return (
    <StandardContainer>
      <Header />
      <Content style={{ alignItems: 'stretch', padding: '0 24px 32px' }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} indicatorColor="primary" textColor="primary">
          <Tab label="Notificações" />
          <Tab label="Usuários" />
        </Tabs>

        {activeTab === 0 && (
          <Paper>
            <Tabs
              value={notificationProvider}
              onChange={(_, value) => {
                setNotificationProvider(value as 'all' | PushProvider);
                setPage(1);
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value="all" label="Todos os canais" />
              <Tab value="firebase" label="Firebase + fallback" />
              <Tab value="expo" label="Expo Notifications" />
            </Tabs>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', padding: 16 }}>
              <TextField
                fullWidth
                label="Buscar por título"
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                style={{ flex: '1 1 280px' }}
              />
              <MuiButton variant="contained" color="primary" onClick={() => openCreateModal()} startIcon={<FiPlus />}>
                Nova notificação
              </MuiButton>
            </div>
            <TableContainer>
              <MuiTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Envio</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Recebidos</TableCell>
                    <TableCell>Aberturas</TableCell>
                    <TableCell>Taxa</TableCell>
                    <TableCell>Ação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                  ) : rows.map(row => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.provider === 'firebase' ? 'Firebase + Expo fallback' : 'Expo Notifications'}</TableCell>
                      <TableCell>{formatDate(row.send_date)}</TableCell>
                      <TableCell>{row.status || (row.scheduled ? 'SCHEDULED' : 'SENT')}</TableCell>
                      <TableCell>{row.total_recipients || 0}</TableCell>
                      <TableCell>{row.total_opened || 0}</TableCell>
                      <TableCell>{Number(row.open_rate || 0).toFixed(2)}%</TableCell>
                      <TableCell>
                        <MuiButton size="small" onClick={() => duplicateNotification(row)} startIcon={<FiCopy />}>Duplicar</MuiButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && !rows.length && <TableRow><TableCell colSpan={9} align="center">Nenhuma notificação encontrada.</TableCell></TableRow>}
                </TableBody>
              </MuiTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRows}
              page={Math.max(0, page - 1)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_, nextPage) => setPage(nextPage + 1)}
              onRowsPerPageChange={() => undefined}
            />
          </Paper>
        )}

        {activeTab === 1 && (
          <Paper style={{ padding: 24, marginTop: 24 }}>
            <Typography variant="h5" gutterBottom>Usuários por provider</Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Selecione o meio de push para visualizar os tokens cadastrados.
            </Typography>
            <Tabs
              value={providerTab}
              onChange={(_, value) => {
                setProviderTab(value as PushProvider);
                setTokenPage(1);
              }}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value="firebase" label="Firebase" />
              <Tab value="expo" label="Expo Notifications" />
            </Tabs>
            {summaryLoading ? <CircularProgress size={24} /> : (() => {
              const item = summary.find(value => value.provider === providerTab);
              const isFirebase = providerTab === 'firebase';
              return (
                <Paper variant="outlined" style={{ padding: 20, marginTop: 20, maxWidth: 360 }}>
                  <Typography variant="subtitle1">
                    {isFirebase ? 'Firebase' : 'Expo Notifications'}
                  </Typography>
                  <Typography variant="h4">{item?.total || 0}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {isFirebase ? 'tokens Firebase ativos' : 'tokens Expo ativos'}
                  </Typography>
                </Paper>
              );
            })()}
            <TableContainer style={{ marginTop: 24 }}>
              <MuiTable size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Push token</TableCell>
                    <TableCell>Plataforma</TableCell>
                    <TableCell>Última atividade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokenRowsLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tokenRows.map(row => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell
                        title={row.token}
                        style={{
                          maxWidth: 420,
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                        }}
                      >
                        {row.token}
                      </TableCell>
                      <TableCell>{row.platform || '—'}</TableCell>
                      <TableCell>{formatDate(row.last_seen_at || undefined)}</TableCell>
                    </TableRow>
                  ))}
                  {!tokenRowsLoading && !tokenRows.length && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        Nenhum token ativo neste canal.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </MuiTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={tokenTotal}
              page={Math.max(0, tokenPage - 1)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_, nextPage) => setTokenPage(nextPage + 1)}
              onRowsPerPageChange={() => undefined}
            />
          </Paper>
        )}
      </Content>

      <CreateNotification
        open={isCreateModalOpen}
        onClose={closeCreateModal}
        onCreated={handleNotificationCreated}
        initialValues={formInitialValues}
      />
    </StandardContainer>
  );
};

export default Notifications;
